import { useMemo, useState } from 'react';
import { useAdminSubmissions, useForceRejectSubmission } from '../useAdmin';
import type { AdminSubmissionRow, ListSubmissionsParams } from '../adminApi';
import { AuthApiError } from '../../account/authApi';
import './AdminGallery.css';

const PAGE_SIZE = 25;

/**
 * Admin gallery oversight (F11). A focused view of what's *currently
 * live* (approved + public) so the operator can scan the catalog and
 * take anything down without going through the full submissions
 * queue. Reuses /admin/submissions under the hood, pinned to
 * status=approved + visibility=public.
 */
export function AdminGallery(): JSX.Element {
  const [submitterEmail, setSubmitterEmail] = useState('');
  const [page, setPage] = useState(0);

  const params = useMemo<ListSubmissionsParams>(
    () => ({
      status: 'approved',
      visibility: 'public',
      submitter_email: submitterEmail.trim() || undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [submitterEmail, page],
  );

  const { data, isLoading, isError, error, isFetching } = useAdminSubmissions(params);
  const reject = useForceRejectSubmission();

  const total = data?.total ?? 0;
  const showing = data?.rows.length ?? 0;
  const startIndex = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const endIndex = page * PAGE_SIZE + showing;
  const canPrev = page > 0;
  const canNext = (page + 1) * PAGE_SIZE < total;

  return (
    <section className="bl-admin-gallery" aria-label="Gallery oversight">
      <header className="bl-admin-gallery__header">
        <div>
          <h2>Gallery</h2>
          <p className="bl-admin-gallery__sub">
            Everything live in the public gallery. Take down to remove something without touching
            the submitter&apos;s other work.
          </p>
        </div>
        <p className="bl-admin-gallery__count" aria-live="polite">
          {isLoading
            ? 'Loading…'
            : total === 0
              ? 'Gallery is empty'
              : `${startIndex.toLocaleString()}–${endIndex.toLocaleString()} of ${total.toLocaleString()} live`}
          {isFetching && !isLoading && ' · refreshing…'}
        </p>
      </header>

      <form
        className="bl-admin-gallery__filters"
        onSubmit={(e) => e.preventDefault()}
        role="search"
      >
        <label className="bl-admin-gallery__field">
          <span className="bl-admin-gallery__field-label">Filter by submitter email</span>
          <input
            type="search"
            value={submitterEmail}
            onChange={(e) => {
              setSubmitterEmail(e.target.value);
              setPage(0);
            }}
            placeholder="ada@school.edu"
            autoComplete="off"
          />
        </label>
      </form>

      {(isError || reject.error) && (
        <p className="bl-admin-gallery__error" role="alert">
          {reject.error instanceof AuthApiError
            ? reject.error.detail || reject.error.message
            : (error || reject.error) instanceof Error
              ? ((error || reject.error) as Error).message
              : 'Could not complete the request.'}
        </p>
      )}

      <ul className="bl-admin-gallery__grid">
        {data?.rows.map((row) => (
          <GalleryItem
            key={row.id}
            row={row}
            onTakedown={() => {
              const reason = window.prompt(
                `Reason for taking "${row.project_name || row.id}" down (shown to the submitter):`,
              );
              if (!reason || !reason.trim()) return;
              reject.mutate({ id: row.id, reason: reason.trim() });
            }}
            busy={reject.isPending}
          />
        ))}
        {!isLoading && data?.rows.length === 0 && (
          <li className="bl-admin-gallery__empty">Nothing live in the gallery right now.</li>
        )}
      </ul>

      <nav className="bl-admin-gallery__pagination" aria-label="Pagination">
        <button
          type="button"
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          disabled={!canPrev}
        >
          ← Previous
        </button>
        <span className="bl-admin-gallery__page-label">
          Page {page + 1}
          {total > 0 && ` of ${Math.max(1, Math.ceil(total / PAGE_SIZE))}`}
        </span>
        <button type="button" onClick={() => setPage((p) => p + 1)} disabled={!canNext}>
          Next →
        </button>
      </nav>
    </section>
  );
}

function GalleryItem({
  row,
  onTakedown,
  busy,
}: {
  row: AdminSubmissionRow;
  onTakedown: () => void;
  busy: boolean;
}): JSX.Element {
  return (
    <li className="bl-admin-gallery__card">
      <div className="bl-admin-gallery__card-head">
        <h3 className="bl-admin-gallery__title">{row.project_name || '(untitled)'}</h3>
        <span className="bl-admin-gallery__kind">{row.project_kind || '—'}</span>
      </div>
      <p className="bl-admin-gallery__meta">by {row.submitter_email || '(unknown)'}</p>
      <p className="bl-admin-gallery__meta">live since {formatDate(row.created_at)}</p>
      <div className="bl-admin-gallery__card-actions">
        <button
          type="button"
          onClick={onTakedown}
          disabled={busy}
          className="bl-admin-gallery__takedown"
        >
          Take down
        </button>
      </div>
    </li>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}
